import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Col, Row, Overlay, Button, Popover } from "react-bootstrap";
import Select from "react-select";
import { connect } from "react-redux";
import { Formik, Form } from "formik";
import { VzPageContainer } from "../../../layouts/VzPageContainer";
import FormikControl from "../../../layouts/Input/FormikControl";
import storage from "../../../apis/Storage";
import ServerSideDataTable from "../../../layouts/DataTables/ServerSideDataTable/ServerSideDataTable";
import Moment from "react-moment";
import {
  GET_DISTRIBUTIONCENTER_DDL,
  GET_DISTRIBUTOR_DDL,
  GET_DELIVERYMAN_TILL_DDL,
} from "../../../Types";

import {
  getDropdownList,
  getDropdownListByPost,
} from "../../Shared/dropdownListActions";
import { Link } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import {
  getDeliverymanTrackingReportData,
  clearDeliverymanTrackingReportData,
} from "./DeliverymanTrackingReportAction";
import { getGlobalSettings } from "../../Settings/GlobalSetting/AppGlobalSettingActions";
import {
  formatDate,
  getDefaultCoordinates,
} from "../../../utilities/FUNCTIONS";
import _ from "lodash";
import { toast } from "react-toastify";
import { getMarkerIcon, MARKER_LEGEND_ITEMS, PIN_H } from "./mapMarkerIcons";

const CustomMarker = ({
  position,
  name,
  shop,
  timing,
  remarks,
  isDetailVisible,
  onDrag,
  index,
  onTimingClick,
  icon,
}) => {
  const [markerPos, setMarkerPos] = useState(position);

  useEffect(() => {
    setMarkerPos(position);
  }, [position]);

  return (
    <>
      <Marker
        position={markerPos}
        icon={icon}
        draggable={true}
        onDragEnd={(e) => {
          const newPos = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
          };
          setMarkerPos(newPos);
          onDrag(newPos, index); // pass to parent
        }}
        onClick={() => onTimingClick(position)}
        title={name}
      />

      {timing && (
        <OverlayView
          position={markerPos}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              transform: `translate(-50%, calc(-100% - ${PIN_H + 6}px))`,
              backgroundColor: timing?.startsWith("S")
                ? "#26c661"
                : timing?.startsWith("E")
                  ? "#ef4747"
                  : "#f39c12",
              padding: "2px 6px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: "500",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
              whiteSpace: "nowrap",
              border: "0.5px solid black",
              minWidth: "40px",
              textAlign: "center",
            }}
          >
            {timing}
          </div>
        </OverlayView>
      )}
    </>
  );
};

const MarkerLegend = ({ nonInductedCheckInEnabled }) => {
  const items = nonInductedCheckInEnabled
    ? MARKER_LEGEND_ITEMS
    : MARKER_LEGEND_ITEMS.filter((item) => item.type !== "shopInvalid");

  return (
    <div
      style={{
        position: "absolute",
        // Cleared up from 10px so this doesn't sit on top of Google's own
        // logo/attribution strip, which the Maps JS API always renders at the
        // very bottom-left of the map and can't be moved or hidden.
        bottom: "34px",
        left: "10px",
        zIndex: 5,
        background: "rgba(255,255,255,0.95)",
        borderRadius: "6px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        padding: "8px 10px",
        fontSize: "12px",
      }}
    >
      {items.map((item) => (
        <div
          key={item.label}
          style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}
        >
          <img
            src={item.icon}
            alt=""
            style={{ width: "15px", height: "20px", marginRight: "6px" }}
          />
          {item.label}
        </div>
      ))}
    </div>
  );
};

const Legend = ({ sectors, shops, agents }) => {
  return (
    <div className="legend px-3" style={{ marginTop: "35px" }}>
      <div className="legend-item">
        <div className="legend-icon legend-sector"></div>
        <div className="legend-label">Sector {sectors}</div>
      </div>
      <div className="legend-item">
        <div className="legend-icon legend-shop"></div>
        <div className="legend-label">Shop {shops}</div>
      </div>
    </div>
  );
};

const polygonsMap = new Map();

const createPolygon = (
  map,
  polygonCoords,
  sectorId,
  onPolygonClick,
  options = {},
) => {
  // Remove existing polygon with the same sectorId
  if (polygonsMap.has(sectorId)) {
    polygonsMap.get(sectorId).setMap(null);
  }

  const polygon = new window.google.maps.Polygon({
    paths: polygonCoords,
    strokeColor: "#FF0000",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: "#FF0000",
    fillOpacity: 0.35,
    clickable: true,
    ...options,
  });

  polygon.setMap(map);
  polygonsMap.set(sectorId, polygon);

  polygon.addListener("click", (e) => {
    onPolygonClick();
  });

  return polygon;
};

const ROUTE_COLORS = [
  "#1E40AF",
  "#DC2626",
  "#16A34A",
  "#9333EA",
  "#EA580C",
  "#0891B2",
];

// Markers within this many meters of each other get spread into a circle so their pins/
// labels don't overlap - not just exact-duplicate coordinates. Set to 0 to revert to the
// old exact-match-only behavior (distance 0 only matches identical lat/long); this constant
// is the only thing that needs to change to turn the feature off.
const NEARBY_MARKER_SPREAD_METERS = 20;

function markerDistanceMeters(lat1, lng1, lat2, lng2) {
  const earthRadiusMeters = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function groupNearbyMarkers(markers, thresholdMeters) {
  const groups = [];
  const used = new Array(markers.length).fill(false);

  markers.forEach((shop, index) => {
    if (used[index]) return;
    const group = [shop];
    used[index] = true;
    const lat = parseFloat(shop.lat);
    const lng = parseFloat(shop.long);

    markers.forEach((other, otherIndex) => {
      if (used[otherIndex]) return;
      const otherLat = parseFloat(other.lat);
      const otherLng = parseFloat(other.long);
      if (markerDistanceMeters(lat, lng, otherLat, otherLng) <= thresholdMeters) {
        group.push(other);
        used[otherIndex] = true;
      }
    });

    groups.push(group);
  });

  return groups;
}

function spreadOverlappingMarkers(markers) {
  const baseOffset = 0.000005; // ~5-6 meters
  const groups = groupNearbyMarkers(markers, NEARBY_MARKER_SPREAD_METERS);
  const adjusted = [];

  for (const group of groups) {
    if (group.length === 1) {
      adjusted.push(group[0]);
    } else {
      // Spread them in a circle, growing slightly for bigger clusters so 4-5
      // nearby stops still stay legible instead of shrinking back together.
      const centerLat = parseFloat(group[0].lat);
      const centerLng = parseFloat(group[0].long);
      const angleStep = (2 * Math.PI) / group.length;
      const offset = baseOffset * (1 + (group.length - 1) * 0.15);

      group.forEach((shop, index) => {
        const angle = index * angleStep;
        adjusted.push({
          ...shop,
          lat: centerLat + Math.sin(angle) * offset,
          long: centerLng + Math.cos(angle) * offset,
          _isAdjusted: true,
        });
      });
    }
  }

  return adjusted;
}

const MemoizedMap = React.memo(
  ({
    shopsList,
    selectedSector,
    selectedShop,
    selectedCityCoords,
    onShopClick,
    onMapClick,
    defaultCoordinates,
    nonInductedCheckInEnabled,
  }) => {
    const normalizedShopsRoutes = useMemo(() => {
      if (!Array.isArray(shopsList)) {
        return [];
      }

      if (Array.isArray(shopsList[0])) {
        return shopsList;
      }

      return shopsList.length > 0 ? [shopsList] : [];
    }, [shopsList]);

    const [map, setMap] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const hasInitializedMap = useRef(false);
    const [adjustedShopsRoutes, setAdjustedShopsRoutes] = useState(
      normalizedShopsRoutes.map((route) => spreadOverlappingMarkers(route)),
    );
    const adjustedShopsList = useMemo(
      () => adjustedShopsRoutes.flat(),
      [adjustedShopsRoutes],
    );

    const [center, setCenter] = useState({
      lat: parseFloat(
        selectedSector.length === 1
          ? selectedSector[0].poligonPoint1.split(",")[1]
          : defaultCoordinates.lat,
      ),
      lng: parseFloat(
        selectedSector.length === 1
          ? selectedSector[0].poligonPoint1.split(",")[0]
          : defaultCoordinates.lng,
      ),
    });

    const { isLoaded } = useJsApiLoader({
      id: "google-map-script",
      // googleMapsApiKey: "AIzaSyCxx8pID_k9_wAbQI_oYL9yYfhlfJntAs4",
      googleMapsApiKey: "AIzaSyDqs07ua04jy-Zz_nYm6GMvOo6WgKZrWNI",
    });

    const handleMapLoad = useCallback(
      (map) => {
        if (!hasInitializedMap.current) {
          setMapInstance(map);
          hasInitializedMap.current = true;
        }
      },
      [selectedSector, shopsList],
    );

    const onUnmount = useCallback(() => setMap(null), []);

    const handlePolygonClick = useCallback(() => {
      onShopClick(null);
    }, []);

    useEffect(() => {
      polygonsMap.forEach((polygon) => polygon.setMap(null));
      polygonsMap.clear();

      if (mapInstance && selectedSector?.length > 0) {
        selectedSector.forEach((sector) => {
          const polygons = [];
          for (let i = 1; i <= 5; i++) {
            const point = sector[`poligonPoint${i}`];
            if (point) {
              const [lng, lat] = point.split(",").map(parseFloat);
              if (!isNaN(lat) && !isNaN(lng)) {
                polygons.push({ lat, lng });
              }
            }
          }

          if (polygons.length >= 3) {
            createPolygon(
              mapInstance,
              polygons,
              sector.id || sector.name,
              handlePolygonClick,
            );
          }
        });
      }
    }, [mapInstance, selectedSector, shopsList]);

    const lineRefs = useRef([]);
    // const [shopMarkers, setShopMarkers] = useState(shopsList);

    useEffect(() => {
      if (!mapInstance) {
        return;
      }

      lineRefs.current.forEach((line) => line.setMap(null));
      lineRefs.current = [];

      const createdLines = [];

      adjustedShopsRoutes.forEach((route, routeIndex) => {
        const pathCoordinates = route
          .filter(
            (shop) =>
              shop.lat && shop.long && shop.lat !== 0 && shop.long !== 0,
          )
          .map((shop) => ({
            lat: parseFloat(shop.lat),
            lng: parseFloat(shop.long),
          }));

        if (pathCoordinates.length < 2) {
          return;
        }

        const newLine = new window.google.maps.Polyline({
          path: pathCoordinates,
          geodesic: true,
          strokeColor: ROUTE_COLORS[routeIndex % ROUTE_COLORS.length],
          strokeOpacity: 1.0,
          strokeWeight: 4,
        });

        newLine.setMap(mapInstance);
        createdLines.push(newLine);
      });

      lineRefs.current = createdLines;
    }, [mapInstance, adjustedShopsRoutes]);

    useEffect(() => {
      setAdjustedShopsRoutes(
        normalizedShopsRoutes.map((route) => spreadOverlappingMarkers(route)),
      );
    }, [normalizedShopsRoutes]);

    useEffect(
      () => () => {
        lineRefs.current.forEach((line) => line.setMap(null));
        lineRefs.current = [];
      },
      [],
    );

    useEffect(() => {
      if (selectedCityCoords != null) {
        setCenter({
          lat: parseFloat(selectedCityCoords.lat),
          lng: parseFloat(selectedCityCoords.lng),
        });
      }
    }, [selectedCityCoords]);

    // Fit the map to whatever the report actually returned (all trips for the
    // day) instead of leaving it parked on the default city center. Keyed off
    // normalizedShopsRoutes (the raw prop data) rather than adjustedShopsRoutes
    // so dragging a marker doesn't re-trigger a refit.
    useEffect(() => {
      if (!mapInstance) return;

      const allPoints = normalizedShopsRoutes
        .flat()
        .filter(
          (shop) => shop.lat && shop.long && shop.lat !== 0 && shop.long !== 0,
        );

      if (allPoints.length === 0) {
        // No report loaded (e.g. right after Reset) - fall back to the default city view
        // instead of leaving the map at whatever tight zoom the last shop cluster fit to,
        // which otherwise renders as a blank/grey tile area centered on empty ground.
        mapInstance.setZoom(15);
        return;
      }

      if (allPoints.length === 1) {
        setCenter({
          lat: parseFloat(allPoints[0].lat),
          lng: parseFloat(allPoints[0].long),
        });
        mapInstance.setZoom(16);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      allPoints.forEach((shop) => {
        bounds.extend({ lat: parseFloat(shop.lat), lng: parseFloat(shop.long) });
      });
      mapInstance.fitBounds(bounds, 60);
    }, [mapInstance, normalizedShopsRoutes]);

    const handleMarkerDrag = (newPos, routeIndex, shopIndex) => {
      setAdjustedShopsRoutes((prevRoutes) =>
        prevRoutes.map((route, currentRouteIndex) =>
          currentRouteIndex !== routeIndex
            ? route
            : route.map((shop, currentShopIndex) =>
                currentShopIndex !== shopIndex
                  ? shop
                  : {
                      ...shop,
                      lat: newPos.lat,
                      long: newPos.lng,
                    },
              ),
        ),
      );
    };

    const mapContainerStyle = {
      height: "100%",
      width: "100%",
    };

    return isLoaded ? (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: "300px",
          flex: 1,
          position: "relative",
        }}
      >
        <MarkerLegend nonInductedCheckInEnabled={nonInductedCheckInEnabled} />
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          onLoad={handleMapLoad}
          onUnmount={onUnmount}
          onClick={onMapClick}
        >
        {adjustedShopsRoutes.map((route, routeIndex) =>
          route.map((item, shopIndex) =>
            item.lat && item.long && item.lat !== 0 && item.long !== 0 ? (
              <CustomMarker
                key={`${routeIndex}-${shopIndex}`}
                index={shopIndex}
                position={{ lat: item.lat, lng: item.long }}
                timing={item.timing}
                remarks={item.remarks}
                name={item.vizId}
                shop={item.shopName}
                icon={getMarkerIcon(item)}
                isDetailVisible={selectedShop?.vizId === item.vizId}
                onTimingClick={(e) => {
                  const _center = mapInstance?.getCenter?.();
                  setCenter(_center);
                  onShopClick(item);
                }}
                onDrag={(pos) => handleMarkerDrag(pos, routeIndex, shopIndex)}
              />
            ) : null,
          ),
        )}

          {selectedShop && (
            <OverlayView
              position={{ lat: selectedShop.lat, lng: selectedShop.long }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -100%)",
                  backgroundColor: "white",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  width: "fit-content",
                  maxWidth: "300px", // Optional: limit maximum width
                  fontSize: "12px",
                  zIndex: 10,
                  marginLeft: "100px",
                  // textAlign: "center",
                  whiteSpace: "nowrap", // Prevents text wrapping
                }}
              >
                {selectedShop.vizId && (
                  <div>
                    <strong>({selectedShop.vizId})</strong>
                  </div>
                )}
                {selectedShop.shopName && (
                  <div>
                    <strong>({selectedShop.shopName})</strong>
                  </div>
                )}
                {selectedShop.checkIn && (
                  <div>
                    <strong>
                      (<Moment format="hh:mm A">{selectedShop.checkIn}</Moment>)
                    </strong>
                  </div>
                )}
                {selectedShop.remarks && (
                  <div>
                    <strong>({selectedShop.remarks})</strong>
                  </div>
                )}
                {selectedShop.isNonInducted && (
                  <div style={{ marginTop: 4, color: "#E53935" }}>
                    <strong>Non-Inducted Shop</strong>
                  </div>
                )}
                {selectedShop.isRevisit && (
                  <div style={{ marginTop: 4, color: "#9333EA" }}>
                    <strong>Revisited Shop</strong>
                  </div>
                )}
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      </div>
    ) : null;
  },
);

const DeliverymanTrackingReport = (props) => {
  const userRights = JSON.parse(storage.get("_userRights"));
  const [target, setTarget] = useState(null);
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  const [distributorList, setDistributorList] = useState([]);
  const [distCenterList, setdistCenterList] = useState([]);
  const [deliverymanNameList, setDeliverymanNameList] = useState([]);

  const [isApplyFilter, setIsApplyFilter] = useState(false);

  const [selectedSector, setSelectedSector] = useState([]);
  // shopsList/visitedShopsList are derived via useMemo further down, not useState - they
  // used to be copied out of props.DeliverymanTrackingReport inside a useEffect, which ran
  // one render behind the top-level stats below (which read the same prop directly) and
  // caused a visible "No trips found" / empty flash on first load before the effect caught up.
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedCityCoords, setSelectedCityCoords] = useState(null);
  // null = "All Trips"; otherwise the real Trip # (e.g. trip.TripNo) to isolate on the map/table.
  const [selectedTripNo, setSelectedTripNo] = useState(null);

  /* ---------------- table state ---------------- */
  const [currentPageNo, setCurrentPageNo] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(5);
  const [searchText, setSearchText] = useState("");

  const _coord = getDefaultCoordinates();

  const [filter, setfilter] = useState({
    deliverymanName: null,
    distributor: null,
    distCenter: null,
    selectedDate: new Date(),
  });

  const openFilterModal = (event) => {
    setShow(!show);
    setTarget(event.target);
  };

  function commonReset(reset = true) {
    if (reset) {
      setSelectedSector([]);
      setSearchText("");
      setSelectedTripNo(null);
      setSelectedShop(null);
      setIsApplyFilter(false);

      setfilter({
        deliverymanName: null,
        distributor: null,
        distCenter: null,
        selectedDate: new Date(),
      });

      setdistCenterList([]);

      setSelectedCityCoords({
        lat: _coord.lat,
        lng: _coord.lng,
      });

      // Otherwise the previous deliveryman's stats/table/map markers stay on
      // screen after Reset, since nothing else re-fetches until Apply is clicked.
      props.clearDeliverymanTrackingReportData();
    }
  }

  function formRequestObject() {
    let requestData = {
      dlmId: filter.deliverymanName?.value ?? null,
      distributorId: filter.distributor?.value ?? null,
      distCenterId: filter.distCenter?.value ?? null,
      selectedDate: filter.selectedDate && formatDate(filter.selectedDate),
    };

    if (userRights != null && userRights?.userType == 'DistributorUser') {
      requestData.distributorId = userRights.distributorId;
    }

    return requestData;
  }

  const handleResetFilters = (resetForm) => {
    resetForm();
    commonReset();
  };

  function fetchReportData(request) {
    let finalRequest = request == null ? formRequestObject() : request;
    props.getDeliverymanTrackingReportData(finalRequest);
  }

  const LoadDataByfilter = (event) => {
    var request = { ...formRequestObject() };

    if (!filter.deliverymanName || filter.deliverymanName.length == 0) {
      toast.info("Please select a deliveryman to fetch the report.");

      setSelectedSector([]);
    } else {
      setIsApplyFilter(true);
      fetchReportData(request);
      setShow(false);
    }
  };

  useEffect(() => {
    props.getDropdownList(
      `api/Distributor/GetDistributorsDDL`,
      GET_DISTRIBUTOR_DDL,
    );
    props.getGlobalSettings();
  }, []);

  const nonInductedCheckInEnabled = useMemo(() => {
    const setting = (props.appGlobalSettings ?? []).find(
      (x) => x.variableName === "Deliveryman NonInducted CheckIn Enabled",
    );
    return setting ? setting.variableValue !== "0" : true;
  }, [props.appGlobalSettings]);

  useEffect(() => {
    if (userRights != null && userRights?.userType == "DistributorUser") {
      let distributorList = props.allDistributorList.filter(
        (x) => x.value == userRights?.distributorId,
      );

      if (distributorList?.length > 0) {
        setfilter({
          ...filter,
          distributor: {
            label: distributorList[0]?.label,
            value: distributorList[0]?.value,
          },
        });
      }
      setDistributorList(
        distributorList.map(function (item) {
          return {
            value: item.value,
            label: item.label,
          };
        }),
      );
    } else if (userRights != null && userRights?.userType == "CoreAppUser") {
      let distributorList = props.allDistributorList.filter((x) =>
        userRights?.distributorId.includes(x.value),
      );
      setDistributorList(
        distributorList.map(function (item) {
          return {
            value: item.value,
            label: item.label,
          };
        }),
      );
    } else {
      setDistributorList(
        props.allDistributorList.map(function (item) {
          return {
            value: item.value,
            label: item.label,
          };
        }),
      );
    }
  }, [props.allDistributorList]);

  useEffect(() => {
    // The API can return a non-array payload (e.g. a null Response, or the whole
    // envelope when Response is falsy) when a distributor has no distribution
    // centers - guard against that instead of letting .filter/.map throw.
    const selectedDistCentersList = Array.isArray(props.selectedDistCentersList)
      ? props.selectedDistCentersList
      : [];

    if (
      (userRights != null && userRights?.userType == "DistributorUser") ||
      userRights?.userType == "CoreAppUser"
    ) {
      let distCenterList = selectedDistCentersList.filter((x) =>
        userRights?.distCenterIds.includes(x.value),
      );
      setdistCenterList(
        distCenterList.map(function (item) {
          return {
            value: item.value,
            label: item.label,
          };
        }),
      );
    } else {
      if (filter.distributor?.value) {
        setdistCenterList(
          selectedDistCentersList.map(function (item) {
            return {
              value: item.value,
              label: item.label,
            };
          }),
        );
      }
    }
  }, [props.selectedDistCentersList]);

  useEffect(() => {
    if (props.deliverymanTills) {
      setDeliverymanNameList(
        props.deliverymanTills.map(function (item) {
          return {
            value: item.id,
            label: item.fullname,
          };
        }),
      );
    }
  }, [props.deliverymanTills]);

  useEffect(() => {
    if (filter.distributor) {
      var id = filter.distributor.value;
      props.getDropdownListByPost(
        `api/DistCenter/GetDistCentersDDL`,
        { distributorIds: id },
        GET_DISTRIBUTIONCENTER_DDL,
      );
    }

    setfilter((prev) => ({ ...prev, distCenter: null }));
  }, [filter.distributor]);

  useEffect(() => {
    const id = filter.distCenter?.value;
    props.getDropdownList(
      `api/DistCenter/GetCenterDeliveryUsersDDL/${id}`,
      GET_DELIVERYMAN_TILL_DDL,
    );

    setfilter((prev) => ({ ...prev, deliverymanName: null }));
  }, [filter.distCenter]);

  // Side effects only (toast + resetting the trip filter on a fresh report) - the actual
  // list data below is derived via useMemo so it's never a render behind these.
  useEffect(() => {
    if (
      !props.DeliverymanTrackingReport?.report?.dlmId &&
      filter.deliverymanName
    ) {
      toast.info("Record Not Found.");
      return;
    }
    setSelectedTripNo(null);
  }, [props.DeliverymanTrackingReport]);

  const normalizedShopsRoutes = useMemo(() => {
    const visitedShopsArrays =
      props.DeliverymanTrackingReport?.report?.vizShopCheckIns ?? [];
    return Array.isArray(visitedShopsArrays[0])
      ? visitedShopsArrays
      : visitedShopsArrays.length > 0
        ? [visitedShopsArrays]
        : [];
  }, [props.DeliverymanTrackingReport]);

  const visitedShopsList = useMemo(
    () =>
      normalizedShopsRoutes
        .flat()
        .filter(
          (x) => !x.serial?.startsWith("S") && !x.serial?.startsWith("E"),
        ),
    [normalizedShopsRoutes],
  );

  const shopsList = useMemo(
    () =>
      normalizedShopsRoutes.map((route) =>
        route.map((item) => ({
          id: "",
          lat: parseFloat(item.latitude),
          long: parseFloat(item.longitude),
          remarks: item.reason || item.remarks,
          checkIn: item.checkInDateTime,
          duration: item.duration,
          sectorPoint: "",
          shopCode: item.vizShopCode,
          shopName: item.vizShopName,
          timing: item.serial,
          vfoName: item.dlmName,
          vizId: item.vizShopCode,
          vizShopId: item.vizShopId,
          isNonInducted: item.isNonInductedShop,
          isRevisit: item.isRevisit,
        })),
      ),
    [normalizedShopsRoutes],
  );

  const columns = [
    { Header: "#", Cell: ({ row: { index } }) => index + 1 },
    { Header: "Trip No", accessor: "tripNo" },
    { Header: "VIZ ID", accessor: "vizShopCode" },
    { Header: "SHOP NAME", accessor: "vizShopName" },
    {
      Header: "CHECK-IN TIME",
      accessor: "checkInDateTime",
      Cell: ({ value }) => (
        <div style={{ width: "75px" }}>
          <Moment format="hh:mm A">{value}</Moment>
        </div>
      ),
    },
    { Header: "DURATION", accessor: "duration" },
    { Header: "DISTANCE FROM PREV", accessor: "distanceFromPrevious" },
  ];

  // Create a helper function
  const formatTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // The backend defaults an open trip's End Point CheckInDateTime to DateTime.MinValue
  // (year 1) rather than null, so a naive formatTime() would render it as a misleading
  // "12:00 AM" instead of showing the trip is still open.
  const isTripClosed = (date) => !!date && new Date(date).getFullYear() > 1900;

  const visitedShopsCount =
    props.DeliverymanTrackingReport?.report?.totalVisitedShops ?? 0;
  const totalShopsInScope =
    (props.DeliverymanTrackingReport?.report?.totalShops ?? 0) +
    (props.DeliverymanTrackingReport?.report?.totalShopsNotIncludedInTrip ??
      0);
  const remainingShopsCount = Math.max(totalShopsInScope - visitedShopsCount, 0);
  const visitedPercentage =
    totalShopsInScope > 0
      ? Math.min((visitedShopsCount / totalShopsInScope) * 100, 100)
      : 0;

  // One option per trip for the day. Each route's first entry is always the Trip Start
  // point and its last entry is always the Trip Close point (the backend appends them
  // unconditionally), so this doesn't depend on the "S"/"S1" serial-label format at all.
  const tripOptions = useMemo(() => {
    return shopsList
      .filter((route) => route.length > 0)
      .map((route) => ({
        tripNo: route[0].shopCode,
        startTime: route[0].checkIn,
        endTime: route[route.length - 1]?.checkIn,
        duration: route[0].duration,
      }));
  }, [shopsList]);

  // react-select options: "All Trips" plus one entry per trip, labeled by trip number only.
  const tripSelectOptions = useMemo(() => {
    return [
      { value: "", label: "All Trips" },
      ...tripOptions.map((option) => ({
        value: option.tripNo,
        label: option.tripNo,
      })),
    ];
  }, [tripOptions]);

  const filteredShopsList =
    selectedTripNo == null
      ? shopsList
      : shopsList.filter((route) => route[0]?.shopCode === selectedTripNo);

  const filteredVisitedShopsList =
    selectedTripNo == null
      ? visitedShopsList
      : visitedShopsList.filter((x) => x.tripNo === selectedTripNo);

  const searchedVisitedShopsList = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return filteredVisitedShopsList;
    return filteredVisitedShopsList.filter((x) =>
      [x.vizShopCode, x.vizShopName, x.tripNo].some(
        (field) => field && field.toLowerCase().includes(term),
      ),
    );
  }, [filteredVisitedShopsList, searchText]);

  const paginatedVisitedShopsList = useMemo(() => {
    const start = currentPageNo * currentPageSize;
    return searchedVisitedShopsList.slice(start, start + currentPageSize);
  }, [searchedVisitedShopsList, currentPageNo, currentPageSize]);

  const handleSearchChanged = (text) => {
    setCurrentPageNo(0);
    setSearchText(text);
  };

  const handlePageSizeChange = (size) => {
    setCurrentPageNo(0);
    setCurrentPageSize(size);
  };

  useEffect(() => {
    setCurrentPageNo(0);
  }, [selectedTripNo]);

  // Rendered inside the Deliveryman sidebar's "Payment Summary" section.
  const statCards = [
    {
      title: "Total Vizlink M2D",
      value:
        "Rs. " +
        (
          props.DeliverymanTrackingReport?.report?.totalVizlinkPayments ?? 0
        ).toLocaleString("en-PK"),
      subtitle:
        "M2D Generated from " +
        (
          props.DeliverymanTrackingReport?.report
            ?.totalShopsAgainstVizlinkPayments ?? 0
        ).toLocaleString("en-PK") +
        " Viz Shops",
      borderColor: "#4a90d9",
    },
    // Non-Vizlink card hidden per request (deliveryman check-in report, 2026-08-03).
    {
      title: "Total Financing",
      value:
        "Rs. " +
        (
          props.DeliverymanTrackingReport?.report?.totalFinancing ?? 0
        ).toLocaleString("en-PK"),
      subtitle:
        "Financing Generated from " +
        (
          props.DeliverymanTrackingReport?.report
            ?.totalShopsAgainstFinancing ?? 0
        ).toLocaleString("en-PK") +
        " Viz Shops",
      borderColor: "#FF9800",
    },
  ];

  return (
    <div>
      <VzPageContainer title="Deliveryman Tracking Report">
        <Row>
          <Col style={{ float: "left" }}>
            <h4>
              <b>Deliveryman Tracking Report</b>
            </h4>
          </Col>
        </Row>
        <Row style={{ marginBottom: "8px" }}>
          <Col
            xs={12}
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: "10px",
            }}
          >
            <div ref={ref}>
              <Button
                onClick={openFilterModal}
                className="my-2"
                variant="outline-primary"
              >
                <i className="fa fa-filter" />
                Filters
              </Button>

              <Overlay
                show={show}
                target={target}
                placement="bottom"
                container={ref}
                containerPadding={20}
                width="430px"
                style={{ width: "430px" }}
              >
                <Popover
                  id="popover-contained"
                  style={{ maxWidth: "360px", width: "360px" }}
                >
                  <Popover.Header as="h3">Filters</Popover.Header>
                  <Popover.Body>
                    <Formik
                      enableReinitialize
                      initialValues={filter}
                      onSubmit={LoadDataByfilter}
                    >
                      {(formik) => (
                        <Form autoComplete="off">
                          <Row>
                            {(userRights == null || userRights?.userType == 'CoreAppUser') && (
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Distributor Name"
                                name="distributor"
                                options={distributorList}
                                isClearable
                                isSearchable
                                isMulti={false}
                                handleOnChange={(e) => {
                                  setfilter((prev) => ({
                                    ...prev,
                                    distributor: e || [],
                                  }));
                                }}
                                value={filter.distributor}
                              />
                            )}

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Distribution Center"
                                name="distCenter"
                                options={distCenterList ?? []}
                                isClearable
                                isSearchable
                                isMulti={false}
                                handleOnChange={(e) => {
                                  setfilter((prev) => ({
                                    ...prev,
                                    distCenter: e || [],
                                  }));
                                }}
                                value={filter.distCenter}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Deliveryman Name"
                                name="deliverymanName"
                                options={deliverymanNameList ?? []}
                                isClearable
                                isSearchable
                                isMulti={false}
                                handleOnChange={(e) => {
                                  setfilter((prev) => ({
                                    ...prev,
                                    deliverymanName: e || [],
                                  }));
                                }}
                                value={filter.deliverymanName}
                              />
                            </Col>
                            <Row style={{ paddingRight: 0 }}>
                              <Col xs={12}>
                                <FormikControl
                                  control="date"
                                  label="Date"
                                  name="selectedDate"
                                  handleOnChange={(value) => {
                                    setfilter({
                                      ...filter,
                                      selectedDate: value,
                                    });
                                  }}
                                  value={filter.selectedDate}
                                />
                              </Col>
                            </Row>
                          </Row>
                          <div className="row" style={{ marginTop: 20 }}>
                            <div className="form-group">
                              <Link
                                onClick={handleResetFilters.bind(
                                  null,
                                  formik.resetForm,
                                  setIsApplyFilter(false),
                                )}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "left",
                                }}
                              >
                                Reset
                              </Link>
                              <Link
                                onClick={() => {
                                  setShow(false);
                                  setTarget(null);
                                  commonReset(isApplyFilter);
                                }}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "right",
                                }}
                              >
                                Cancel
                              </Link>
                              <div className="float-right">
                                <Button
                                  type="submit"
                                  className="btn btn-primary"
                                  style={{
                                    marginRight: "10px",
                                    float: "right",
                                  }}
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>

            {tripOptions.length > 1 && (
              // react-select instead of a plain <select>: it's fully client-rendered from
              // tripSelectOptions (already in memory by the time this is visible at all,
              // since it only shows once tripOptions.length > 1), so opening it can't show
              // a briefly-empty native OS dropdown that then populates a moment later.
              //
              // Color: rather than guess the theme's primary color as a hex value (which
              // twice came out visibly different from the real Filters button), this wraps
              // Select in an actual "btn btn-outline-primary" element - the same classes
              // Filters uses - and makes react-select's own control borderless/transparent
              // so the genuine Bootstrap CSS rule supplies the border/text color, not a copy.
              <div
                className="btn btn-outline-primary my-2"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "12px",
                  width: "135px",
                  padding: "0 4px 0 10px",
                  cursor: "default",
                }}
              >
                <i className="fa fa-road" style={{ marginRight: "4px" }} />
                <Select
                  value={tripSelectOptions.find(
                    (o) => o.value === (selectedTripNo ?? ""),
                  )}
                  onChange={(opt) => setSelectedTripNo(opt.value || null)}
                  options={tripSelectOptions}
                  isSearchable={false}
                  styles={{
                    container: (base) => ({ ...base, flex: 1, minWidth: 0 }),
                    control: (base) => ({
                      ...base,
                      minHeight: "36px",
                      border: "none",
                      background: "transparent",
                      boxShadow: "none",
                    }),
                    valueContainer: (base) => ({ ...base, padding: 0 }),
                    singleValue: (base) => ({
                      ...base,
                      margin: 0,
                      color: "inherit",
                      fontWeight: 600,
                      fontSize: "13px",
                    }),
                    placeholder: (base) => ({ ...base, margin: 0, color: "inherit" }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: "0 4px",
                      color: "inherit",
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: "13px",
                      fontWeight: state.isSelected ? 700 : 400,
                      backgroundColor: state.isFocused ? "#FFEDE4" : "#fff",
                      color: "#333",
                    }),
                    indicatorSeparator: () => ({ display: "none" }),
                    menu: (base) => ({ ...base, zIndex: 20, color: "#333" }),
                  }}
                />
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={9} style={{ display: "flex", flexDirection: "column" }}>
            <div
              className="card"
              style={{
                flex: 1,
                minHeight: "600px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* <Map /> */}
              <MemoizedMap
                shopsList={filteredShopsList}
                selectedSector={selectedSector}
                selectedShop={selectedShop}
                selectedCityCoords={selectedCityCoords}
                onShopClick={(shop) => setSelectedShop(shop)}
                onMapClick={() => setSelectedShop(null)}
                defaultCoordinates={_coord}
                nonInductedCheckInEnabled={nonInductedCheckInEnabled}
              />
            </div>
          </Col>
          {/* Always the same height as the map card (flex:1), whether there's trip data or
              not - the empty/sparse states below are built to fill that height gracefully
              (icon + message, not a single bare line) instead of leaving it forced-tall
              with dead gaps. */}
          <Col xs={3} style={{ display: "flex", flexDirection: "column" }}>
            <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Deliveryman Information Panel */}

              <div
                style={{
                  background: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  borderRadius: "8px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    background: "#ff6a00",
                    padding: "8px 18px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={`https://ui-avatars.com/api/?name=${props.DeliverymanTrackingReport?.report?.dlmName || "DLM"}&background=0D8ABC&color=fff`}
                    alt=""
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: 14,
                      border: "2px solid #fff",
                    }}
                  />

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 20,
                        lineHeight: 1.2,
                      }}
                    >
                      {props.DeliverymanTrackingReport?.report?.dlmName ?? ""}
                    </div>

                    <div
                      style={{
                        color: "rgba(255,255,255,.85)",
                        fontSize: 13,
                        marginTop: 3,
                      }}
                    >
                      {props.DeliverymanTrackingReport?.report?.tripDate ?? ""}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#DDFBEA",
                      color: "#168C61",
                      padding: "4px 12px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Active
                  </div>
                </div>

                {/* Body: 3 sections (Deliveryman Info / Trip Info / Visit Summary), each one
                    flex item. justifyContent:space-between spreads any leftover height as
                    gaps between them rather than inside them, and the empty/no-trips state
                    below is now a real icon+message block (not a bare line of text) so it
                    carries its own visual weight instead of leaving those gaps feeling dead. */}
                <div
                  style={{
                    padding: 8,
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {/* Deliveryman Information */}
                    <div
                      style={{
                        color: "#8A8A8A",
                        fontWeight: 700,
                        fontSize: 12,
                        marginBottom: 14,
                      }}
                    >
                      DELIVERYMAN INFORMATION
                    </div>

                    <div style={{ fontSize: 14, marginBottom: 10 }}>
                      <strong>Distributor:</strong>{" "}
                      <span style={{ color: "#555" }}>
                        {props.DeliverymanTrackingReport?.report
                          ?.distributorName || "—"}
                      </span>
                    </div>

                    <div style={{ fontSize: 14, marginBottom: 10 }}>
                      <strong>Distribution Center:</strong>{" "}
                      <span style={{ color: "#555" }}>
                        {props.DeliverymanTrackingReport?.report
                          ?.distCenterName || "—"}
                      </span>
                    </div>

                    <div style={{ fontSize: 14, marginBottom: 10 }}>
                      <strong>Report Date:</strong>{" "}
                      <span style={{ color: "#555" }}>
                        {filter.selectedDate
                          ? new Date(filter.selectedDate).toLocaleDateString(
                              "en-US",
                              { day: "2-digit", month: "short", year: "numeric" },
                            )
                          : "—"}
                      </span>
                    </div>

                    <div style={{ fontSize: 14 }}>
                      <strong>Shops Assigned Today:</strong>{" "}
                      <span style={{ color: "#555" }}>
                        {(props.DeliverymanTrackingReport?.report?.totalShops ??
                          0) +
                          (props.DeliverymanTrackingReport?.report
                            ?.totalShopsNotIncludedInTrip ?? 0)}
                      </span>
                    </div>
                  </div>

                  <div>
                  <hr style={{ margin: "8px 0", borderColor: "#ECECEC" }} />

                  {/* Trip Information */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ color: "#8A8A8A", fontWeight: 700, fontSize: 12 }}>
                      TRIP INFORMATION
                    </div>

                    {/* Compact visited/remaining chips - the overall shop-coverage numbers
                        used to live as two big boxes under "Visit Summary"; that section now
                        shows the payment stat cards instead, so the coverage numbers surface
                        here, next to the trip list they explain. Labeled (not just icon+number)
                        so it reads on its own without hovering for the tooltip. */}
                    <div style={{ display: "flex", gap: 6 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#F3FFF8",
                          color: "#17A55A",
                          border: "1px solid #D9F1E5",
                        }}
                        title="Visited Shops"
                      >
                        <i className="fa fa-check-circle" />
                        {visitedShopsCount} Visited
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#FFF3EA",
                          color: "#D97706",
                          border: "1px solid #FBE0C4",
                        }}
                        title="Remaining Shops"
                      >
                        <i className="fa fa-hourglass-half" />
                        {remainingShopsCount} Left
                      </span>
                    </div>
                  </div>

                  {/* Visited/total progress bar - kept here since it's part of the same
                      shop-coverage story as the chips above. */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                      fontSize: 12,
                      color: "#777",
                    }}
                  >
                    <span>
                      Visited{" "}
                      <strong style={{ color: "#333" }}>
                        ({visitedShopsCount}/{totalShopsInScope})
                      </strong>
                    </span>
                    <strong style={{ color: "#ff6a00" }}>
                      {Math.round(visitedPercentage)}%
                    </strong>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 100,
                      background: "#F0F0F0",
                      overflow: "hidden",
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        width: `${visitedPercentage}%`,
                        height: "100%",
                        background: "#ff6a00",
                      }}
                    />
                  </div>

                  {/* One card per trip - same representation whether there's 1 trip or
                      several, instead of a plain table for multi-trip days and separate
                      plain rows for single-trip days. Also fixes "12:00 AM" showing for an
                      open trip: the backend defaults an unset End time to DateTime.MinValue
                      rather than null, so isTripClosed() is what decides "Active" vs a
                      real end time, not just whether a value is present. */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {tripOptions.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "18px 12px",
                          border: "1px dashed #E2E4E8",
                          borderRadius: 8,
                        }}
                      >
                        <i
                          className="fa fa-road"
                          style={{ fontSize: 20, color: "#C7CBD1" }}
                        />
                        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: "#777" }}>
                          No trips yet today
                        </div>
                        <div style={{ marginTop: 2, fontSize: 12, color: "#999" }}>
                          Trips will show up here once started
                        </div>
                      </div>
                    )}
                    {tripOptions.map((trip) => {
                      const closed = isTripClosed(trip.endTime);
                      return (
                        <div
                          key={trip.tripNo}
                          style={{
                            border: "1px solid #ECECEC",
                            borderRadius: 8,
                            padding: "10px 12px",
                            background: "#FAFAFA",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 8,
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>
                              <i
                                className="fa fa-road"
                                style={{ color: "#ff6a00", marginRight: 6 }}
                              />
                              Trip # {trip.tripNo}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: closed ? "#F0F0F0" : "#DDFBEA",
                                color: closed ? "#777" : "#168C61",
                              }}
                            >
                              {closed ? "Completed" : "Active"}
                            </span>
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div style={{ textAlign: "left" }}>
                              <div style={{ fontSize: 10, color: "#999" }}>START</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>
                                {formatTime(trip.startTime)}
                              </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                              <div style={{ fontSize: 10, color: "#999" }}>END</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>
                                {closed ? formatTime(trip.endTime) : "—"}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 10, color: "#999" }}>DURATION</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>
                                {trip.duration || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>

                  <div>
                  <hr style={{ margin: "8px 0", borderColor: "#ECECEC" }} />

                  {/* Payment Summary - the Total Vizlink M2D / Total Financing cards used to
                      float above the map (flush right, matched to this sidebar's width via a
                      matching Col xs={3}); moved here into the sidebar itself so they sit with
                      the rest of the deliveryman's stats, stacked to fit the narrower column. */}
                  <div
                    style={{
                      color: "#8A8A8A",
                      fontWeight: 700,
                      fontSize: 12,
                      marginBottom: 14,
                    }}
                  >
                    PAYMENT SUMMARY
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {statCards.map((card) => (
                      <div
                        key={card.title}
                        style={{
                          backgroundColor: "#FAFAFA",
                          borderRadius: 8,
                          padding: "10px 12px",
                          borderLeft: `4px solid ${card.borderColor}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#666",
                            fontWeight: 500,
                            marginBottom: 4,
                          }}
                        >
                          {card.title}
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: "bold",
                            color: "#333",
                            marginBottom: 6,
                          }}
                        >
                          {card.value}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#999",
                            borderTop: "1px solid #eee",
                            paddingTop: 6,
                          }}
                        >
                          {card.subtitle}
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <div className="card">
          <Row style={{ marginTop: "10px" }}>
            <div className="card-header">
              <h2 className="card-title">
                <b>Shop Visit Details</b>
              </h2>
            </div>
            <Col xs={12}>
              <ServerSideDataTable
                data={paginatedVisitedShopsList}
                columns={columns}
                totalRecords={searchedVisitedShopsList.length}
                pageNo={currentPageNo}
                myPageSize={currentPageSize}
                goToNextPage={setCurrentPageNo}
                goToPreviousPage={setCurrentPageNo}
                pageSizeChange={handlePageSizeChange}
                searchChanged={handleSearchChanged}
              />
            </Col>
          </Row>
        </div>
      </VzPageContainer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    DeliverymanTrackingReport: state.DeliverymanTrackingReport
      ? state.DeliverymanTrackingReport
      : [],
    allDistributorList: state.ddlList.allDistributorList
      ? state.ddlList.allDistributorList
      : [],
    selectedDistCentersList: state.ddlList.selectedDistCentersList
      ? state.ddlList.selectedDistCentersList
      : [],
    deliverymanTills: state.ddlList.deliverymanTills
      ? state.ddlList.deliverymanTills
      : [],
    rsoUsers: state.ddlList.rsosList ? state.ddlList.rsosList : [],
    cities: state.ddlList.cities ? state.ddlList.cities : [],
    appGlobalSettings: state.AppGlobalSetting ? state.AppGlobalSetting : [],
  };
};
export default connect(mapStateToProps, {
  getDropdownList,
  getDropdownListByPost,
  getDeliverymanTrackingReportData,
  clearDeliverymanTrackingReportData,
  getGlobalSettings,
})(DeliverymanTrackingReport);
